// FeaturePlansPanel.js
import AnalysisCoverageBlock from "./AnalysisCoverageBlock";
import LightweightDiagramsPanel from "./LightweightDiagramsPanel";
import WorkBriefGuidePreview from "./WorkBriefGuidePreview";
import WorkBriefGuidedFlow from "./WorkBriefGuidedFlow";
import TaskFocusWorkspace, { TaskExpandButton } from "./TaskFocusWorkspace";
import {
  TASK_LABEL,
  TASKS_LABEL,
} from "./taskCopy";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { RefreshCw, Download, ChevronDown, ChevronUp, Trash2, Copy } from "lucide-react";
import api from "./api";
import { useAuth } from "./AuthContext";
import { canMutateRepos } from "./orgPermissions";
import { useFeaturePlanStatusPolling } from "./useFeaturePlanStatusPolling";
import { getRelevantCodebaseGuideEntries } from "./codebaseGuideRelevance";
import { guideCategoryLabel, guidePriorityLabel } from "./codebaseGuideConstants";
import TaskWorkspace from "./TaskWorkspace";
import FullTaskPage from "./FullTaskPage";
import EngineeringHandoffPanel from "./EngineeringHandoffPanel";
import TasksHomePage from "./TasksHomePage";
import DecisionReviewPanel from "./DecisionReviewPanel";
import TaskBuilder from "./TaskBuilder";
import TaskRow from "./TaskRow";
import ConnectDeliveredWork from "./ConnectDeliveredWork";
import AcceptanceReviewSection from "./AcceptanceReviewSection";
import { taskPath } from "./taskRoutes";
import { canonicalizeSection, currentActionSection } from "./taskSections";
import { findSimilarActiveTask, isEvaluationTask, taskShortTitle } from "./taskShortTitle";
import { deriveTaskDisplayCopy } from "./taskDisplayCopy";
import { projectTaskWorkspace } from "./taskWorkspaceProjection";
import { projectTasksHome } from "./tasksHomeModel";
import "./FeaturePlansPanel.css";
import "./UserDashboard.css"; // For status-badge classes

/** Format evidence label: unitName (unitType) Lx–Ly. Backward compatible when fields missing. */
function formatEvidenceLabel(ev) {
  const parts = [];
  if (ev.unitName) parts.push(ev.unitName);
  if (ev.unitType) parts.push(`(${ev.unitType})`);
  if (ev.startLine != null && ev.endLine != null) {
    parts.push(`L${ev.startLine}–L${ev.endLine}`);
  } else if (ev.startLine != null) {
    parts.push(`L${ev.startLine}+`);
  }
  return parts.join(" ").trim();
}

function getEvidenceTrustMeta(ev) {
  const rawSnippet = (ev?.snippet || "").trim();
  const hasSnippet = rawSnippet.length > 0;
  const looksSynthetic = /synthetic|placeholder|inferred/i.test(rawSnippet);
  if (!hasSnippet) {
    return {
      trustLabel: "Inferred evidence (no retrieved snippet shown)",
      trustClass: "inferred",
      snippetPreview: "No retrieved snippet available for this evidence item.",
    };
  }
  if (looksSynthetic) {
    return {
      trustLabel: "Inferred/placeholder evidence",
      trustClass: "inferred",
      snippetPreview: rawSnippet.length > 200 ? rawSnippet.substring(0, 200) + "..." : rawSnippet,
    };
  }
  return {
    trustLabel: null,
    trustClass: "retrieved",
    snippetPreview: rawSnippet.length > 200 ? rawSnippet.substring(0, 200) + "..." : rawSnippet,
  };
}

/** Axios errors are normalized in api.js — recover status/body for preflight handling. */
function getApiErrorResponse(err) {
  return err?.response ?? err?.originalError?.response ?? null;
}

function WorkBriefContextSnapshot({ snapshot }) {
  if (!snapshot || !Array.isArray(snapshot.entries)) return null;
  return (
    <div className="plan-content-block" data-testid="work-brief-context-snapshot">
      <h5>Context used for this {TASK_LABEL}</h5>
      <p className="work-brief-context-note">
        Captured when this {TASK_LABEL} was created. Later Codebase Guide edits do not change it.
      </p>
      {snapshot.entries.length === 0 ? (
        <p data-testid="work-brief-context-empty">No eligible Codebase Guide context was available.</p>
      ) : (
        <ul className="work-brief-context-list">
          {snapshot.entries.map((entry) => (
            <li key={entry.entryId} data-testid="work-brief-context-entry">
              <div className="work-brief-context-entry-heading">
                <strong>{entry.title}</strong>
                <span>
                  {guideCategoryLabel(entry.category)} · {guidePriorityLabel(entry.priority)}
                </span>
              </div>
              <pre className="plan-intelligence-text">{entry.body}</pre>
            </li>
          ))}
        </ul>
      )}
      <p className="work-brief-context-audit">
        Snapshot {String(snapshot.contentHash || "unavailable").slice(0, 12)}
        {snapshot.truncated ? ` · ${snapshot.omittedEntryCount || 0} entries omitted` : ""}
      </p>
    </div>
  );
}

const FeaturePlansPanel = ({
  repo,
  initialSelectedPlanId = null,
  initialTaskSection = null,
  initialTaskDiscuss = null,
  initialTaskFocus = null,
  initialTaskItem = null,
  tasksFilter = "all",
  onOpenRoadmap = null,
  onTaskRoute = null,
}) => {
  const { user } = useAuth();
  const mutateAllowed = canMutateRepos(user);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const appliedInitialPlanIdRef = useRef(null);
  const [featureRequestText, setFeatureRequestText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warning, setWarning] = useState("");
  const [deferredPollCount, setDeferredPollCount] = useState(0);
  const [pollingError, setPollingError] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [copyingPrompt, setCopyingPrompt] = useState(false);
  const [copyPromptSuccess, setCopyPromptSuccess] = useState(false);
  const [intentJsonExpanded, setIntentJsonExpanded] = useState(false);
  const [evidenceByLayerExpanded, setEvidenceByLayerExpanded] = useState(true);
  const [evidenceByFileExpanded, setEvidenceByFileExpanded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [latestDeliveryReview, setLatestDeliveryReview] = useState(null);
  const [reviewsByPlanId, setReviewsByPlanId] = useState({});
  const [composingNew, setComposingNew] = useState(false);
  const [similarTask, setSimilarTask] = useState(null);
  const [createAnyway, setCreateAnyway] = useState(false);
  const createIdempotencyKeyRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `task-create-${Date.now()}`
  );
  const [taskNotFound, setTaskNotFound] = useState(false);
  const [handoffAgreementStatus, setHandoffAgreementStatus] = useState(null);
  const [guideEntries, setGuideEntries] = useState([]);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideError, setGuideError] = useState("");
  const [roadmapItems, setRoadmapItems] = useState([]);
  const detailPaneRef = useRef(null);
  const expandOriginRef = useRef(null);
  const [focusMode, setFocusMode] = useState(false);
  const [focusSaveState, setFocusSaveState] = useState("Saved");
  const [focusEscapeBlocked, setFocusEscapeBlocked] = useState(false);
  const [workspaceSection, setWorkspaceSection] = useState(null);
  const [guidedWorkspaceState, setGuidedWorkspaceState] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);
  const sectionChoiceRef = useRef(false);
  const [discussContext, setDiscussContext] = useState(() =>
    initialTaskDiscuss || initialTaskFocus || initialTaskItem
      ? {
          section: initialTaskDiscuss || initialTaskFocus,
          focusSection: initialTaskFocus || initialTaskDiscuss,
          itemId: initialTaskItem,
        }
      : null
  );
  const [taskDocumentTick, setTaskDocumentTick] = useState(0);
  const [taskDocumentFrozen, setTaskDocumentFrozen] = useState(false);

  const handleLatestReviewChange = useCallback((review) => {
    setLatestDeliveryReview(review || null);
  }, []);

  const workspaceProjection = useMemo(
    () =>
      projectTaskWorkspace({
        planStatus: selectedPlan?.status,
        planError: selectedPlan?.error,
        contractState: guidedWorkspaceState?.contractState,
        hasEngAck:
          typeof guidedWorkspaceState?.hasEngAck === "boolean"
            ? guidedWorkspaceState.hasEngAck
            : String(selectedPlan?.status || "").toLowerCase() === "completed",
        latestReview: latestDeliveryReview,
        orchestrationSnapshot: guidedWorkspaceState?.orchestrationSnapshot,
        openQuestionCount: guidedWorkspaceState?.openQuestionCount,
        approvalAllowed: guidedWorkspaceState?.approvalAllowed,
        intentFreezeReady: guidedWorkspaceState?.intentFreezeReady,
        hasDraft: guidedWorkspaceState?.hasDraft,
        promiseCount: guidedWorkspaceState?.promiseCount,
        frozenVersionNumber: guidedWorkspaceState?.frozenVersionNumber,
      }),
    [selectedPlan?.status, selectedPlan?.error, guidedWorkspaceState, latestDeliveryReview]
  );
  const visibleSection =
    canonicalizeSection(workspaceSection) || currentActionSection(workspaceProjection);
  const showPlanEditor =
    visibleSection === "task" &&
    expandedStage === "plan" &&
    workspaceProjection.editingAllowed;
  const showHandoffEditor = false;

  useEffect(() => {
    if (!selectedPlan?._id) {
      setLatestDeliveryReview(null);
      setGuidedWorkspaceState(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [orchRes, reviewRes] = await Promise.all([
          api.get(`/feature-plans/${selectedPlan._id}/orchestration`).catch(() => ({ data: null })),
          api.get(`/feature-plans/${selectedPlan._id}/acceptance-reviews`).catch(() => ({ data: { reviews: [] } })),
        ]);
        if (cancelled) return;
        const payload = orchRes?.data;
        const snapshot =
          payload?.snapshot ||
          (payload?.phase || payload?.currentEffectiveState || payload?.furthestPhaseReached
            ? payload
            : null);
        const reviews = Array.isArray(reviewRes?.data?.reviews) ? reviewRes.data.reviews : [];
        if (reviews[0]) setLatestDeliveryReview(reviews[0]);
        if (snapshot) {
          setGuidedWorkspaceState((prev) => ({
            ...(prev || {}),
            orchestrationSnapshot: snapshot,
          }));
        }
      } catch {
        // Overview can still render from the selected plan and any later guided-flow updates.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPlan?._id]);

  const emitTaskRoute = useCallback(
    (taskId, section, context = {}) => {
      if (typeof onTaskRoute !== "function" || !repo?._id) return;
      onTaskRoute(
        taskPath({
          repositoryId: repo._id,
          taskId,
          section,
          discuss: context.discuss,
          focus: context.focus,
          item: context.item,
        })
      );
    },
    [onTaskRoute, repo?._id]
  );

  useEffect(() => {
    if (!selectedPlan || sectionChoiceRef.current) return;
    const next = currentActionSection(workspaceProjection);
    setWorkspaceSection(next);
    emitTaskRoute(selectedPlan._id, next);
    // Sync the default section when the Task or its action changes, not on every projection object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedPlan/workspaceProjection are read for id and action only
  }, [selectedPlan?._id, workspaceProjection.defaultSection, workspaceProjection.primaryAction, emitTaskRoute]);

  const handleWorkspaceSectionChange = useCallback((next, context = {}) => {
    const section = canonicalizeSection(next) || currentActionSection(workspaceProjection);
    sectionChoiceRef.current = true;
    setWorkspaceSection(section);
    if (context.discuss || context.focus || context.item || context.section) {
      setDiscussContext({
        ...(typeof context === "object" ? context : {}),
        section: context.section || context.discuss || context.focus || null,
        itemId: context.itemId || context.item || null,
        focusSection: context.focus || context.section || null,
      });
    }
    if (selectedPlan?._id) {
      emitTaskRoute(selectedPlan._id, section, {
        discuss: section === "conversation" ? context.discuss || context.section || discussContext?.section : null,
        focus: section === "task" ? context.focus || context.section || null : null,
        item: context.item || context.itemId || null,
      });
    }
  }, [discussContext?.section, emitTaskRoute, selectedPlan?._id, workspaceProjection]);

  const loadCodebaseGuide = useCallback(async () => {
    if (!repo?._id) return;
    try {
      setGuideLoading(true);
      setGuideError("");
      const response = await api.listCodebaseGuide(repo._id);
      setGuideEntries(response.data?.entries || []);
    } catch (err) {
      setGuideEntries([]);
      setGuideError(
        err.response?.data?.error ||
          `Could not load Codebase Guide rules. ${TASKS_LABEL} still work.`
      );
    } finally {
      setGuideLoading(false);
    }
  }, [repo?._id]);

  useEffect(() => {
    loadCodebaseGuide();
  }, [loadCodebaseGuide]);

  useEffect(() => {
    if (!repo?._id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.getRepoRoadmap(repo._id);
        if (!cancelled) {
          setRoadmapItems(response.data?.roadmap?.items || []);
        }
      } catch {
        if (!cancelled) setRoadmapItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repo?._id]);

  const visiblePlans = useMemo(
    () =>
      plans.filter(
        (plan) => !isEvaluationTask(plan) && !plan.founderHidden && !plan.archivedAt
      ),
    [plans]
  );

  const linkedRoadmapItem = useMemo(() => {
    if (!selectedPlan?._id) return null;
    return (
      (roadmapItems || []).find(
        (item) => item.workBriefId && String(item.workBriefId) === String(selectedPlan._id)
      ) || null
    );
  }, [roadmapItems, selectedPlan]);

  const createGuidePreview = useMemo(() => {
    return getRelevantCodebaseGuideEntries(guideEntries, null, {
      mode: "create",
      audience: "both",
      limit: 8,
      draftRequestText: featureRequestText,
    });
  }, [guideEntries, featureRequestText]);

  const detailGuidePreview = useMemo(() => {
    if (!selectedPlan) {
      return { entries: [], reasonsById: {} };
    }
    return getRelevantCodebaseGuideEntries(guideEntries, selectedPlan, {
      mode: "detail",
      audience: "both",
      limit: 8,
    });
  }, [guideEntries, selectedPlan]);

  useEffect(() => {
    if (!repo?._id) return;
    try {
      const draft = sessionStorage.getItem(`featurePlanDraft-${repo._id}`);
      if (draft) setFeatureRequestText(draft);
    } catch {
      /* ignore bad draft storage */
    }
  }, [repo?._id]);

  // Format date in a simple human-readable way
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // For older dates, show date
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
  };

  const fetchPlans = useCallback(async () => {
    if (!repo?._id) return;

    try {
      setLoading(true);
      setError("");
      
      const queryParams = new URLSearchParams();
      queryParams.append("repoId", repo._id);
      queryParams.append("offset", "0");
      queryParams.append("limit", "100");
      
      const response = await api.get(`/feature-plans?${queryParams.toString()}`);
      setPlans(response.data.plans || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load feature plans");
    } finally {
      setLoading(false);
    }
  }, [repo?._id]);

  // Fetch plans when repo changes
  useEffect(() => {
    if (repo?._id) {
      fetchPlans();
    }
  }, [repo?._id, fetchPlans]);

  useEffect(() => {
    const completed = (plans || [])
      .filter((plan) => String(plan.status || "").toLowerCase() === "completed")
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      .slice(0, 20);
    if (completed.length === 0) {
      setReviewsByPlanId({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        completed.map(async (plan) => {
          try {
            const [reviewRes, orchRes] = await Promise.all([
              api.get(`/feature-plans/${plan._id}/acceptance-reviews`).catch(() => ({ data: { reviews: [] } })),
              api.get(`/feature-plans/${plan._id}/orchestration`).catch(() => ({ data: null })),
            ]);
            const reviews = Array.isArray(reviewRes?.data?.reviews) ? reviewRes.data.reviews : [];
            const payload = orchRes?.data;
            const snapshot =
              payload?.snapshot ||
              (payload?.phase || payload?.currentEffectiveState || payload?.furthestPhaseReached
                ? payload
                : null);
            return [plan._id, { review: reviews[0] || null, snapshot }];
          } catch {
            return [plan._id, null];
          }
        })
      );
      if (!cancelled) setReviewsByPlanId(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [plans]);

  const projectionForPlan = (plan) => {
    const packed = reviewsByPlanId[plan?._id] || {};
    return projectTaskWorkspace({
      planStatus: plan?.status,
      planError: plan?.error,
      latestReview: packed.review || (selectedPlan?._id === plan?._id ? latestDeliveryReview : null),
      orchestrationSnapshot:
        packed.snapshot ||
        (selectedPlan?._id === plan?._id ? guidedWorkspaceState?.orchestrationSnapshot : null),
      hasEngAck: String(plan?.status || "").toLowerCase() === "completed",
      hasDraft: Boolean(plan?.acceptanceCriteria?.length || plan?.planIntelligence),
    });
  };

  const handleSelectPlan = async (plan, options = {}) => {
    setSelectedPlan(plan);
    setTaskDocumentFrozen(false);
    setLatestDeliveryReview(null);
    setHandoffAgreementStatus(null);
    setGuidedWorkspaceState(null);
    setExpandedStage(null);
    const requested = canonicalizeSection(options.section);
    sectionChoiceRef.current = Boolean(requested) && !options.followAction;
    const nextSection = requested || currentActionSection(projectionForPlan(plan));
    setWorkspaceSection(nextSection);
    appliedInitialPlanIdRef.current = plan._id;
    setTaskNotFound(false);
    if (!options.skipRoute || !requested) {
      emitTaskRoute(plan._id, nextSection);
    }
    setDeferredPollCount(0);
    setIntentJsonExpanded(false);
    setEvidenceByLayerExpanded(true);
    setEvidenceByFileExpanded(false);
    setWarning("");
    setPollingError("");
    setDetailLoading(true);

    try {
      const response = await api.get(`/feature-plans/${plan._id}`);
      const detail = response.data;
      setSelectedPlan(detail);
      setPlans((prev) =>
        prev.map((p) => (p._id === detail._id ? { ...p, ...detail } : p))
      );
    } catch (err) {
      if (err.response?.status === 404) {
        setTaskNotFound(true);
        setSelectedPlan(null);
        return;
      }
      setError(
        err.response?.data?.error ||
          "Failed to load full plan details. Showing summary from list."
      );
    } finally {
      setDetailLoading(false);
      requestAnimationFrame(() => {
        detailPaneRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    }
  };

  const openMissionTask = (planId, section) => {
    const plan = plans.find((item) => String(item._id) === String(planId));
    if (!plan) return;
    setComposingNew(false);
    setFocusMode(false);
    handleSelectPlan(plan, {
      section: canonicalizeSection(section) || undefined,
      followAction: true,
    });
  };

  const openFocusForPlan = (plan) => {
    if (!plan) return;
    setFocusMode(false);
    handleSelectPlan(plan, {
      section: currentActionSection(projectionForPlan(plan)),
      followAction: true,
    });
  };

  const focusWasOpenRef = useRef(false);
  const exitFocusMode = useCallback(() => {
    setFocusMode(false);
  }, []);

  useEffect(() => {
    if (focusMode) {
      focusWasOpenRef.current = true;
      return;
    }
    if (!focusWasOpenRef.current) return;
    focusWasOpenRef.current = false;
    expandOriginRef.current?.focus?.();
  }, [focusMode]);

  // Open a Task when navigated from Roadmap (or similar).
  useEffect(() => {
    if (initialSelectedPlanId === "new") {
      appliedInitialPlanIdRef.current = "new";
      setComposingNew(true);
      setTaskNotFound(false);
      setSelectedPlan(null);
      return;
    }
    if (!initialSelectedPlanId || !plans.length) return;
    if (appliedInitialPlanIdRef.current === initialSelectedPlanId) return;
    const plan = plans.find((p) => p._id === initialSelectedPlanId);
    if (!plan) return;
    appliedInitialPlanIdRef.current = initialSelectedPlanId;
    handleSelectPlan(plan, {
      section: initialTaskSection,
      skipRoute: Boolean(initialTaskSection),
      followAction: !initialTaskSection,
    });
    // handleSelectPlan is intentionally not a dep — run once per new id + plans load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedPlanId, initialTaskSection, plans]);

  // Keep the open Task section in sync when the URL section changes (tabs, back/forward, deep links).
  useEffect(() => {
    if (!initialSelectedPlanId || !selectedPlan?._id) return;
    if (String(selectedPlan._id) !== String(initialSelectedPlanId)) return;
    const next = canonicalizeSection(initialTaskSection);
    if (!next) return;
    if (canonicalizeSection(workspaceSection) === next) return;
    sectionChoiceRef.current = true;
    setWorkspaceSection(next);
  }, [initialSelectedPlanId, initialTaskSection, selectedPlan?._id, workspaceSection]);

  // Leaving a deep-linked Task or New Task (e.g. Tasks tab) must return to the home list.
  useEffect(() => {
    if (initialSelectedPlanId) return;
    appliedInitialPlanIdRef.current = null;
    setSelectedPlan(null);
    setTaskNotFound(false);
    setComposingNew(false);
  }, [initialSelectedPlanId]);

  useEffect(() => {
    if (tasksFilter === "review") setComposingNew(false);
  }, [tasksFilter]);

  useEffect(() => {
    if (!initialSelectedPlanId || loading) return;
    const found = plans.some((plan) => String(plan._id) === String(initialSelectedPlanId));
    if (initialSelectedPlanId === "new") return;
    if (!found && plans.length > 0) setTaskNotFound(true);
  }, [initialSelectedPlanId, plans, loading]);

  const handleRegenerate = async () => {
    if (!selectedPlan?._id) return;

    // Show confirmation dialog
    if (
      !window.confirm(
        "This will use additional credits. Continue?"
      )
    ) {
      return;
    }

    try {
      setRegenerating(true);
      setError("");
      setWarning("");
      setPollingError(""); // Clear polling errors
      setDeferredPollCount(0); // Reset deferred poll count

      const response = await api.post(
        `/feature-plans/${selectedPlan._id}/regenerate`
      );

      // Handle 200/201/202 (regeneration started)
      if (response.status === 200 || response.status === 201 || response.status === 202) {
        const updatedPlan = response.data.plan || response.data;
        
        // Update selected plan (polling hook will automatically start if status is generating)
        setSelectedPlan(updatedPlan);
        
        // Update in list
        setPlans((prev) =>
          prev.map((plan) =>
            plan._id === updatedPlan._id ? updatedPlan : plan
          )
        );

        // If deferred, show warning
        if (response.status === 202) {
          setWarning("Generation was deferred. Redis unavailable. The plan is pending and will be processed when Redis is available.");
        }
        // If generating, polling will start automatically via the hook
        // When it completes, handleTerminal will fetch full plan including lastRegeneratedAt
      }
    } catch (err) {
      // Handle 409 (Already generating)
      if (err.response?.status === 409) {
        setError("Plan is already generating. Please wait for it to complete.");
      }
      // Handle 402 (Insufficient Credits)
      else if (err.response?.status === 402) {
        setError(
          err.response?.data?.message ||
            "You need credits to regenerate a feature plan. Please purchase credits to continue."
        );
      }
      // Handle other errors
      else {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to regenerate feature plan"
        );
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleExport = async () => {
    if (!selectedPlan?._id || selectedPlan.status !== "completed") {
      return;
    }

    try {
      setExporting(true);
      setError("");

      const response = await api.get(
        `/feature-plans/${selectedPlan._id}/export`,
        {
          responseType: "blob",
        }
      );

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: "text/markdown" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `feature-plan-${selectedPlan._id}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to export feature plan"
      );
    } finally {
      setExporting(false);
    }
  };

  const handleCopyCursorPrompt = async () => {
    if (!selectedPlan?._id || selectedPlan.status !== "completed") return;
    try {
      setCopyingPrompt(true);
      setError("");
      setCopyPromptSuccess(false);
      const response = await api.get(
        `/feature-plans/${selectedPlan._id}/cursor-prompt?variant=build`,
        { responseType: "text" }
      );
      const text = typeof response.data === "string" ? response.data : String(response.data);
      await navigator.clipboard.writeText(text);
      setCopyPromptSuccess(true);
      setTimeout(() => setCopyPromptSuccess(false), 3000);
    } catch (err) {
      const status = err.response?.status;
      const msg =
        status === 409
          ? "Plan not completed yet."
          : status === 403
            ? "Access denied."
            : status === 404
              ? "Plan not found."
              : err.response?.data?.error ||
                err.message ||
                "Failed to copy Cursor prompt";
      setError(msg);
    } finally {
      setCopyingPrompt(false);
    }
  };

  const clearSelectedTask = () => {
    setSelectedPlan(null);
    setGuidedWorkspaceState(null);
    setLatestDeliveryReview(null);
    setExpandedStage(null);
    sectionChoiceRef.current = false;
    setWorkspaceSection("plan");
    setComposingNew(false);
    appliedInitialPlanIdRef.current = null;
    emitTaskRoute(undefined);
  };

  const handleDeletePlan = async (planId) => {
    const plan = plans.find((item) => String(item._id) === String(planId));
    if (!plan?._id) return;
    const title = taskShortTitle(plan.featureRequestText) || TASK_LABEL;
    if (!window.confirm(`Archive “${title}”? You can restore it later.`)) {
      return;
    }
    try {
      setDeletingId(plan._id);
      setError("");
      await api.put(`/feature-plans/${plan._id}/archive`, { archived: true });
      setPlans((prev) => prev.filter((item) => item._id !== plan._id));
      setWarning("");
      if (selectedPlan?._id === plan._id) {
        clearSelectedTask();
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to delete Task"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlan?._id) return;
    await handleDeletePlan(selectedPlan._id);
  };

  // Update plan in both selectedPlan and plans array
  const updatePlanStatus = useCallback((statusData) => {
    // Update selected plan
    setSelectedPlan((prev) => {
      if (!prev || prev._id !== statusData._id) return prev;
      return {
        ...prev,
        status: statusData.status,
        updatedAt: statusData.updatedAt,
        jobId: statusData.jobId,
        error: statusData.error,
        generationMeta: statusData.generationMeta,
        creditsCharged: statusData.credits?.creditsCharged,
        creditsDeducted: statusData.credits?.creditsDeducted,
        ...(statusData.workReceipt && { workReceipt: statusData.workReceipt }),
      };
    });

    // Update plan in list
    setPlans((prev) =>
      prev.map((plan) =>
        plan._id === statusData._id
          ? {
              ...plan,
              status: statusData.status,
              updatedAt: statusData.updatedAt,
              jobId: statusData.jobId,
              error: statusData.error,
              generationMeta: statusData.generationMeta,
              creditsCharged: statusData.credits?.creditsCharged,
              creditsDeducted: statusData.credits?.creditsDeducted,
              ...(statusData.workReceipt && { workReceipt: statusData.workReceipt }),
            }
          : plan
      )
    );
  }, []);

  // Handle terminal state (completed, failed, deferred, timeout)
  const handleTerminal = useCallback(
    async (statusData, reason) => {
      if (reason === "completed") {
        // Fetch full plan
        try {
          const response = await api.get(`/feature-plans/${statusData._id}`);
          const fullPlan = response.data;
          setSelectedPlan(fullPlan);
          // Update in list too
          setPlans((prev) =>
            prev.map((plan) =>
              plan._id === fullPlan._id ? fullPlan : plan
            )
          );
        } catch (err) {
          setError(
            err.response?.data?.error ||
              "Failed to fetch full plan details. Status updated but some details may be missing."
          );
          // Still update with status data we have
          updatePlanStatus(statusData);
        }
      } else if (reason === "timeout") {
        // Show timeout message and mark plan with timeout error
        setWarning("Still generating, refresh the plan later.");
        const timeoutStatusData = {
          ...statusData,
          error: {
            ...statusData.error,
            code: "TIMEOUT",
            message: "Generation exceeded 5 minute time limit",
          },
        };
        updatePlanStatus(timeoutStatusData);
      } else {
        // For failed, deferred - just update with status data
        updatePlanStatus(statusData);
      }
    },
    [updatePlanStatus]
  );

  // Determine if polling should be enabled
  const shouldPoll =
    selectedPlan &&
    (selectedPlan.status === "generating" ||
      (selectedPlan.status === "pending" &&
        selectedPlan.error?.code === "REDIS_UNAVAILABLE"));

  // Normal polling for generating plans
  useFeaturePlanStatusPolling({
    planId: selectedPlan?._id || null,
    enabled:
      shouldPoll &&
      selectedPlan?.status === "generating" &&
      selectedPlan?.error?.code !== "REDIS_UNAVAILABLE",
    onUpdate: updatePlanStatus,
    onTerminal: handleTerminal,
    onError: (err) => {
      setPollingError(
        err.response?.data?.error ||
          err.message ||
          "Failed to check plan status. Polling will continue."
      );
    },
  });

  // Separate deferred polling (slower, limited attempts)
  useEffect(() => {
    const planId = selectedPlan?._id;
    const isDeferred =
      selectedPlan?.status === "pending" &&
      selectedPlan?.error?.code === "REDIS_UNAVAILABLE";

    if (!planId || !isDeferred || deferredPollCount >= 6) {
      return;
    }

    let currentPollCount = deferredPollCount;

    const deferredPollInterval = setInterval(async () => {
      // Check if we've exceeded max attempts
      if (currentPollCount >= 6) {
        clearInterval(deferredPollInterval);
        return;
      }

      try {
        const response = await api.get(`/feature-plans/${planId}/status`);
        const statusData = response.data;

        // If status changed from pending, stop deferred polling
        if (statusData.status !== "pending") {
          currentPollCount = 6;
          setDeferredPollCount(6);
          clearInterval(deferredPollInterval);
          handleTerminal(statusData, statusData.status);
          return;
        }

        // Update status
        updatePlanStatus(statusData);

        // Increment poll count
        currentPollCount += 1;
        setDeferredPollCount(currentPollCount);

        // Stop after 6 attempts
        if (currentPollCount >= 6) {
          clearInterval(deferredPollInterval);
        }
      } catch (err) {
        // Surface error but continue polling
        setPollingError(
          err.response?.data?.error ||
            err.message ||
            "Failed to check deferred plan status. Polling will continue."
        );
        // Continue polling on error, but increment count
        currentPollCount += 1;
        setDeferredPollCount(currentPollCount);
        if (currentPollCount >= 6) {
          clearInterval(deferredPollInterval);
        }
      }
    }, 10000); // 10 seconds

    return () => clearInterval(deferredPollInterval);
  }, [
    selectedPlan?._id,
    selectedPlan?.status,
    selectedPlan?.error?.code,
    deferredPollCount,
    updatePlanStatus,
    handleTerminal,
  ]);

  const submitCreatePlan = async ({
    featureRequestText: nextText,
    sourceArtifactIds,
    forceDuplicate,
    selectAfterCreate = true,
  } = {}) => {
    const text = String(nextText ?? featureRequestText ?? "").trim();
    const artifactIds = Array.isArray(sourceArtifactIds) ? sourceArtifactIds : [];
    if (!repo?._id || (!text && artifactIds.length === 0)) {
      return null;
    }
    if (submitting) return null;

    const similar = findSimilarActiveTask(plans, text);
    if (similar && !createAnyway && !forceDuplicate) {
      setSimilarTask(similar);
      return null;
    }

    try {
      setSubmitting(true);
      setError("");
      setWarning("");
      setSimilarTask(null);

      const response = await api.post("/feature-plans", {
        repoId: repo._id,
        featureRequestText: text,
        sourceArtifactIds: artifactIds,
        idempotencyKey: createIdempotencyKeyRef.current,
        originKind: "user",
      });

      if (repo?._id) {
        sessionStorage.removeItem(`featurePlanDraft-${repo._id}`);
      }

      if (response.status === 201 || response.status === 200) {
        const newPlan = response.data.plan || response.data;
        setPlans((prev) => {
          if (prev.some((item) => String(item._id) === String(newPlan._id))) return prev;
          return [newPlan, ...prev];
        });
        setFeatureRequestText("");
        setCreateAnyway(false);
        createIdempotencyKeyRef.current =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `task-create-${Date.now()}`;
        if (selectAfterCreate) {
          setSelectedPlan(newPlan);
          handleSelectPlan(newPlan, { skipRoute: false });
        }
        return newPlan;
      }
      if (response.status === 202) {
        const newPlan = response.data.plan || response.data;
        setPlans((prev) => [newPlan, ...prev]);
        setWarning(
          "Generation was deferred. Redis unavailable. The plan is pending and will be processed when Redis is available."
        );
        setFeatureRequestText("");
        if (selectAfterCreate) {
          setSelectedPlan(newPlan);
        }
        return newPlan;
      }
      return null;
    } catch (err) {
      const errResponse = getApiErrorResponse(err);
      const errData = errResponse?.data;

      if (errResponse?.status === 402) {
        setError(
          errData?.message ||
            "You need credits to start a new feature plan generation. Please purchase credits to continue."
        );
      } else if (errResponse?.status === 429) {
        setError("Rate limit exceeded. Please wait a moment and try again.");
      } else {
        setError(
          errData?.message ||
            errData?.error ||
            err?.message ||
            "Failed to create feature plan"
        );
      }
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const completedPlanIds = (plans || [])
    .filter((plan) => String(plan.status || "").toLowerCase() === "completed")
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .slice(0, 20)
    .map((plan) => plan._id);
  const reviewsReady =
    !loading &&
    (completedPlanIds.length === 0 ||
      completedPlanIds.every((id) => Object.prototype.hasOwnProperty.call(reviewsByPlanId, id)));
  const home = projectTasksHome({
    repo,
    plans: visiblePlans,
    reviewsByPlanId,
    loading,
    reviewsReady,
  });

  if (!repo) {
    return <div className="feature-plans-panel" data-testid="work-briefs-panel">No repository selected</div>;
  }

  return (
    <div className="feature-plans-panel" data-testid="work-briefs-panel">
      <div className="feature-plans-header" data-landing="true" hidden>
        <h2 className="visually-hidden">
          {TASKS_LABEL} for {repo.owner}/{repo.name}
        </h2>
        <p className="work-briefs-subtitle" hidden>
          Specific pieces of planned work. Describe a {TASK_LABEL.toLowerCase()}, generate a plan, then hand off and review delivery.
        </p>
      </div>

      <details
        className="work-brief-readiness"
        data-testid="work-brief-readiness"
        hidden={Boolean(selectedPlan)}
      >
        <summary>Repository readiness</summary>
        <p className="work-brief-readiness-hint">
          Indexing and analysis coverage for evidence quality. Full detail lives under Codebase Guide.
        </p>
        <AnalysisCoverageBlock repo={repo} />
      </details>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {warning && (
        <div className="warning-message">
          {warning}
        </div>
      )}

      {pollingError && (
        <div className="error-message">
          {pollingError}
        </div>
      )}

      <div className="feature-plans-content">
        {taskNotFound && !selectedPlan ? (
          <section className="founder-panel" data-testid="task-not-found">
            <h2>This Task is not available</h2>
            <p>It may have been archived, or you may not have access in this workspace.</p>
            <button
              type="button"
              className="founder-primary"
              onClick={() => {
                setTaskNotFound(false);
                emitTaskRoute(undefined);
              }}
            >
              Back to repository Tasks
            </button>
          </section>
        ) : null}
        {!selectedPlan && !taskNotFound && composingNew && tasksFilter !== "review" ? (
          <section className="start-task-workspace" data-testid="start-task-workspace">
            {similarTask ? (
              <div className="start-task-similar" data-testid="start-task-similar-warning">
                <p>
                  A similar Task already exists
                  {similarTask.featureRequestText
                    ? `: ${taskShortTitle(similarTask.featureRequestText)}`
                    : "."}
                </p>
                <div className="start-task-similar-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setComposingNew(false);
                      handleSelectPlan(similarTask, { section: "task" });
                    }}
                  >
                    Open existing Task
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateAnyway(true);
                      setSimilarTask(null);
                    }}
                  >
                    Create separate Task anyway
                  </button>
                </div>
              </div>
            ) : null}
            <WorkBriefGuidePreview
              variant="create"
              entries={createGuidePreview.entries}
              loading={guideLoading}
              error={guideError}
            />
            <TaskBuilder
              repo={repo}
              plans={visiblePlans}
              mutateAllowed={mutateAllowed}
              forceCreate={createAnyway}
              onSimilarTask={setSimilarTask}
              onCreateTask={submitCreatePlan}
              onCancel={() => {
                setComposingNew(false);
                setSimilarTask(null);
                setCreateAnyway(false);
                emitTaskRoute(undefined);
              }}
              onCreated={(created) => {
                setPlans((prev) => {
                  if (prev.some((item) => String(item._id) === String(created._id))) return prev;
                  return [created, ...prev];
                });
              }}
              onOpenTask={(created) => {
                if (created?._id) handleSelectPlan(created, { section: "task" });
              }}
              onSent={(created) => {
                if (created?._id) handleSelectPlan(created, { section: "task" });
              }}
            />
          </section>
        ) : null}
        {!selectedPlan && !taskNotFound && !composingNew && tasksFilter === "review" ? (
          <DecisionReviewPanel repo={repo} mutateAllowed={mutateAllowed} />
        ) : null}
        {!selectedPlan && !taskNotFound && !composingNew ? (
          <TasksHomePage
            repo={repo}
            plans={visiblePlans}
            reviewsByPlanId={reviewsByPlanId}
            loading={loading}
            reviewsReady={reviewsReady}
            mutateAllowed={mutateAllowed}
            archivingId={deletingId}
            filter={tasksFilter}
            onArchiveTask={handleDeletePlan}
            onNewTask={() => {
              setComposingNew(true);
              emitTaskRoute("new");
            }}
            onOpenTask={openMissionTask}
          />
        ) : null}

        {selectedPlan ? (
        <div className="feature-plans-master-detail has-selected-task" data-testid="task-split-view">
          <div className="feature-plans-list-pane feature-plans-section">
            <h3>{tasksFilter === "review" ? "Reviews" : TASKS_LABEL}</h3>
            <p className="task-sidebar-hint">
              {tasksFilter === "review"
                ? "Work waiting for a review. Open a row to see the result."
                : "Grouped by where each Task stands."}
            </p>
            <div className="plan-list" data-testid="task-inbox-list">
              {(tasksFilter === "review"
                ? home.groups.filter((group) => group.id === "readyToReview")
                : home.groups
              )
                .filter((group) => group.items.length > 0)
                .map((group) => (
                  <section key={group.id} className="task-sidebar-group" data-testid={`task-sidebar-${group.id}`}>
                    <p className="task-sidebar-group-title">{group.title}</p>
                    {group.items.map((item) => (
                      <TaskRow
                        key={item.id}
                        item={item}
                        compact
                        selected={selectedPlan?._id === item.id}
                        mutateAllowed={mutateAllowed}
                        archivingId={deletingId}
                        onOpen={openMissionTask}
                        onArchive={handleDeletePlan}
                      />
                    ))}
                  </section>
                ))}
            </div>
          </div>

          <div
            className="feature-plans-detail-pane feature-plans-section"
            ref={detailPaneRef}
            data-testid="feature-plan-detail-pane"
          >
            {!selectedPlan ? (
              <div className="feature-plans-detail-placeholder">
                <h3>{TASK_LABEL} detail</h3>
                <p>Select a {TASK_LABEL} from the list to inspect criteria, handoff, and acceptance review.</p>
              </div>
            ) : (
              <>
                <div className="task-workspace-toolbar">
                  <button
                    type="button"
                    className="task-workspace-back"
                    data-testid="task-workspace-back"
                    onClick={clearSelectedTask}
                  >
                    All {TASKS_LABEL}
                  </button>
                  <TaskExpandButton
                    testId={`task-expand-${selectedPlan._id}`}
                    label="Open Task"
                    onClick={() => openFocusForPlan(selectedPlan)}
                  />
                  {mutateAllowed ? (
                    <button
                      type="button"
                      className="task-workspace-delete"
                      data-testid="task-delete-current"
                      onClick={handleDelete}
                      disabled={Boolean(deletingId)}
                    >
                      <Trash2 size={16} />
                      {deletingId ? "Archiving…" : `Archive ${TASK_LABEL}`}
                    </button>
                  ) : null}
                </div>
                <div className="feature-plans-detail-header" hidden>
                  <div>
                    <h3>{TASK_LABEL}</h3>
                    <p className="feature-plans-detail-request-label">Original request</p>
                    <p
                      className="feature-plans-detail-request"
                      data-testid="work-brief-original-request"
                    >
                      {selectedPlan.featureRequestText || "No task description"}
                    </p>
                  </div>
                  <div className="feature-plans-detail-meta">
                    <span
                      className={`status-badge ${selectedPlan.status}`}
                      title={workspaceProjection.statusLabel}
                      data-testid="work-brief-status-badge"
                    >
                      {workspaceProjection.statusLabel}
                    </span>
                    <span className="feature-plans-detail-date">
                      Updated {formatDate(selectedPlan.updatedAt)}
                    </span>
                  </div>
                </div>

                {detailLoading && (
                  <div className="status-indicator generating" data-testid="work-brief-detail-loading">
                    <span className="spinner">⟳</span>
                    <span>Loading {TASK_LABEL} details…</span>
                  </div>
                )}
                {(selectedPlan.lowConfidence === true ||
                  selectedPlan.surfaceConfidence === "Low" ||
                  selectedPlan.impactConfidence === "Low") && (
                  <div className="warning-message">
                    This plan is exploratory: evidence confidence is limited. Verify key files before implementation.
                  </div>
                )}

            {/* Timeout message (still generating after 5 minutes) */}
            {selectedPlan.status === "generating" &&
              warning.includes("Still generating") && (
                <div className="warning-message">
                  Still preparing — refresh the plan later if this takes too long.
                </div>
              )}

            <>
            <TaskFocusWorkspace
              open={focusMode}
              title={deriveTaskDisplayCopy(selectedPlan).title}
              repoLabel={`${repo.owner}/${repo.name}`}
              statusLabel={workspaceProjection.statusLabel}
              saveState={focusSaveState}
              onExit={exitFocusMode}
              escapeBlocked={focusEscapeBlocked}
            >
            <TaskWorkspace
              title={deriveTaskDisplayCopy(selectedPlan).title}
              repoLabel={`${repo.owner}/${repo.name}`}
              projection={workspaceProjection}
              section={visibleSection}
              onSectionChange={handleWorkspaceSectionChange}
              showHeader={!focusMode}
            >
            <div
              hidden={visibleSection !== "conversation"}
              className="task-page-conversation"
              data-testid="task-conversation-page"
            >
              <TaskBuilder
                repo={repo}
                plans={visiblePlans}
                plan={selectedPlan}
                mutateAllowed={mutateAllowed}
                embedded
                hidePreview
                frozen={taskDocumentFrozen}
                saveState={focusSaveState}
                discussContext={discussContext}
                onOpenTask={(section, itemId) =>
                  handleWorkspaceSectionChange("task", { focus: section || "requirements", item: itemId })
                }
                onTaskMutated={(data, section) => {
                  setTaskDocumentTick((value) => value + 1);
                  setTaskDocumentFrozen(Boolean(data?.document?.frozen));
                  if (section) {
                    setDiscussContext((prev) => ({ ...(prev || {}), focusSection: section, section }));
                  }
                }}
                onCreated={() => {}}
                onSent={(created) =>
                  handleSelectPlan(created || selectedPlan, { section: "task" })
                }
              />
            </div>
            <div hidden={visibleSection !== "task"} className="task-page-document" data-testid="task-document-page">
              <FullTaskPage
                key={`${selectedPlan._id}-${taskDocumentTick}`}
                plan={selectedPlan}
                repo={repo}
                projection={workspaceProjection}
                mutateAllowed={mutateAllowed}
                discussContext={discussContext}
                onOpenConversation={(context) => {
                  if (context) setDiscussContext(context);
                  handleWorkspaceSectionChange("conversation", context || {});
                }}
                onDiscuss={(context) => {
                  setDiscussContext(context);
                  handleWorkspaceSectionChange("conversation", context || {});
                }}
                onDocumentChange={(data) => setTaskDocumentFrozen(Boolean(data?.document?.frozen))}
                onApproved={() => handleWorkspaceSectionChange("engineering")}
              />
            </div>
            {visibleSection === "engineering" ? (
              <EngineeringHandoffPanel
                plan={selectedPlan}
                repo={repo}
                mutateAllowed={mutateAllowed}
                onSaved={(updated) => {
                  if (!updated?._id) return;
                  setSelectedPlan(updated);
                  setPlans((prev) =>
                    prev.map((item) => (item._id === updated._id ? { ...item, ...updated } : item))
                  );
                }}
                onUnfrozen={(data) => {
                  setTaskDocumentFrozen(Boolean(data?.document?.frozen));
                  setTaskDocumentTick((value) => value + 1);
                  if (selectedPlan?._id) {
                    setSelectedPlan((prev) =>
                      prev
                        ? {
                            ...prev,
                            productDocument: data?.document || prev.productDocument,
                          }
                        : prev
                    );
                  }
                  handleWorkspaceSectionChange("task");
                }}
              />
            ) : null}
            {visibleSection === "delivery" ? (
              <ConnectDeliveredWork
                featurePlanId={selectedPlan._id}
                repo={repo}
                taskTitle={deriveTaskDisplayCopy(selectedPlan).title || selectedPlan.featureRequestText || ""}
                latestReview={latestDeliveryReview}
                deliveredWork={selectedPlan.deliveredWork}
                mutateAllowed={mutateAllowed}
                autoFocus={workspaceProjection.primaryAction?.id === "connect_delivered_work" || workspaceProjection.status === "Stale"}
                onReviewComplete={handleLatestReviewChange}
                onOpenReview={() => handleWorkspaceSectionChange("review")}
              />
            ) : null}
            {visibleSection === "review" ? (
              <>
                {!latestDeliveryReview &&
                !selectedPlan.deliveredWork?.sourceInput &&
                !selectedPlan.deliveredWork?.originalInput &&
                !selectedPlan.deliveredWork?.url &&
                !selectedPlan.deliveredWork?.branchName ? (
                  <section className="task-review-needs-delivery" data-testid="review-needs-delivery">
                    <h3>Connect the work first</h3>
                    <p>
                      Paste the pull request or branch on Delivery. Review is where you see
                      whether that work matches this Task.
                    </p>
                    <button
                      type="button"
                      className="founder-primary"
                      onClick={() => handleWorkspaceSectionChange("delivery")}
                    >
                      Connect the pull request or branch
                    </button>
                  </section>
                ) : (
                  <>
                    {workspaceProjection.status === "Stale" ? (
                      <section className="task-review-needs-delivery" data-testid="review-stale-connect">
                        <h3>This review is out of date</h3>
                        <p>
                          The code changed after this review. Confirm the latest pull request or
                          branch on Delivery, then come back here for the new result.
                        </p>
                        <button
                          type="button"
                          className="founder-primary"
                          onClick={() => handleWorkspaceSectionChange("delivery")}
                        >
                          Connect the latest work
                        </button>
                      </section>
                    ) : null}
                    <AcceptanceReviewSection
                      featurePlanId={selectedPlan._id}
                      planStatus={selectedPlan.status}
                      repoId={repo?._id}
                      reviewEligible={
                        Boolean(guidedWorkspaceState?.hasEngAck) ||
                        workspaceProjection.stage === "Ready for review" ||
                        workspaceProjection.stage === "Waiting for decision" ||
                        workspaceProjection.stage === "Completed" ||
                        workspaceProjection.stage === "Needs attention"
                      }
                      onLatestReviewChange={handleLatestReviewChange}
                      heading="Review the work"
                      intro="This is the review of the work you connected. The pull-request or branch link is on Delivery."
                      decisionAllowed={workspaceProjection.decisionControlsAllowed}
                      initialReview={latestDeliveryReview}
                      hideSourceForm
                    />
                  </>
                )}
              </>
            ) : null}
            {showPlanEditor || showHandoffEditor ? (
            <WorkBriefGuidedFlow
              repoId={repo?._id}
              workBriefId={selectedPlan._id}
              featureRequestText={selectedPlan.featureRequestText || ""}
              planStatus={selectedPlan.status}
              planError={selectedPlan.error || null}
              hasAcceptanceCriteria={
                Array.isArray(selectedPlan.acceptanceCriteria) &&
                selectedPlan.acceptanceCriteria.length > 0
              }
              onRetryPlanning={handleRegenerate}
              latestDeliveryReview={latestDeliveryReview}
              onLatestReviewChange={handleLatestReviewChange}
              onAgreementStatusChange={setHandoffAgreementStatus}
              layout="inline"
              workspaceSection={visibleSection}
              onWorkspaceStateChange={setGuidedWorkspaceState}
              onRequestExpand={(event) =>
                openFocusForPlan(selectedPlan, event?.currentTarget || expandOriginRef.current)
              }
              readOnly={!mutateAllowed}
              onSaveStateChange={setFocusSaveState}
              onEscapeBlockedChange={setFocusEscapeBlocked}
              onEngineerStartPack={(pack) => {
                setSelectedPlan((prev) =>
                  prev && pack ? { ...prev, engineerStartPack: pack } : prev
                );
              }}
            />
            ) : null}

            {visibleSection === "activity" ? (
            <div
              className={`work-brief-agreement-strip ${
                handoffAgreementStatus?.ready
                  ? "work-brief-agreement-strip--ready"
                  : "work-brief-agreement-strip--not-ready"
              }`}
              data-testid="work-brief-agreement-strip"
            >
              <strong data-testid="work-brief-agreement-strip-label">
                {handoffAgreementStatus?.label || "Not ready"}
              </strong>
              <span data-testid="work-brief-agreement-strip-detail">
                {handoffAgreementStatus?.ready
                  ? `${handoffAgreementStatus.detail || "Both parties agreed on the promises."} Build Prompt now includes the agreed handoff; plan internals below are supporting only.`
                  : handoffAgreementStatus?.headline
                    ? `${handoffAgreementStatus.headline}. ${TASK_LABEL} internals below are supporting material, not the agreed handoff.`
                    : "Handoff agreement status loads with the promise draft."}
              </span>
            </div>
            ) : null}

            <div
              hidden={visibleSection !== "activity"}
              data-testid="task-workspace-activity-legacy"
            >
            {handoffAgreementStatus?.ready &&
            handoffAgreementStatus?.deliverySummary?.promiseCount > 0 ? (
              <details
                className="work-brief-handoff-overview"
                data-testid="work-brief-handoff-overview"
                open
              >
                <summary>
                  Agreed handoff overview (
                  {handoffAgreementStatus.deliverySummary.promiseCount} promise
                  {handoffAgreementStatus.deliverySummary.promiseCount === 1 ? "" : "s"}
                  {handoffAgreementStatus.deliverySummary.clarificationCount
                    ? ` · ${handoffAgreementStatus.deliverySummary.clarificationCount} clarification${
                        handoffAgreementStatus.deliverySummary.clarificationCount === 1
                          ? ""
                          : "s"
                      }`
                    : ""}
                  )
                </summary>
                <div className="work-brief-handoff-overview-body">
                  <p className="work-brief-handoff-hint">
                    This is what Build Prompt sends as the authoritative contract. Plan steps and
                    diagrams below support delivery — they do not replace these promises.
                  </p>
                  <ol className="work-brief-handoff-promise-list">
                    {handoffAgreementStatus.deliverySummary.promises.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ol>
                  {(handoffAgreementStatus.deliverySummary.clarifications || []).length > 0 ? (
                    <>
                      <h6>Clarifications</h6>
                      <ul className="work-brief-handoff-qa-list">
                        {handoffAgreementStatus.deliverySummary.clarifications.map((c) => (
                          <li key={`${c.question}:${c.answer}`}>
                            <strong>{c.question}</strong>
                            <span>
                              {c.kind === "not_required" ? "Not required: " : "Answer: "}
                              {c.answer}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                  {(handoffAgreementStatus.deliverySummary.notes || []).length > 0 ? (
                    <>
                      <h6>Notes</h6>
                      <ul>
                        {handoffAgreementStatus.deliverySummary.notes.map((n) => (
                          <li key={n}>{n}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                  {(handoffAgreementStatus.deliverySummary.openQuestions || []).length > 0 ? (
                    <>
                      <h6>Still open (warnings)</h6>
                      <ul>
                        {handoffAgreementStatus.deliverySummary.openQuestions.map((q) => (
                          <li key={q}>{q}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              </details>
            ) : null}

            <details
              className="work-brief-technical-details"
              data-testid="work-brief-technical-details"
              defaultOpen
            >
              <summary>
                {handoffAgreementStatus?.ready
                  ? "Supporting plan details (diagrams, steps, files — not the contract)"
                  : `Technical details (${TASK_LABEL} internals, diagrams, plan content — handoff not agreed yet)`}
              </summary>

            <div className="selected-plan-detail">
              <div className="plan-detail-row">
                <strong>ID:</strong> {selectedPlan._id}
              </div>
              <div className="plan-detail-row">
                <strong>Status:</strong>{" "}
                <span className={`status-badge ${selectedPlan.status}`}>
                  {selectedPlan.status}
                </span>
              </div>
              <div className="plan-detail-row">
                <strong>Created:</strong> {formatDate(selectedPlan.createdAt)}
              </div>
              <div className="plan-detail-row">
                <strong>Updated:</strong> {formatDate(selectedPlan.updatedAt)}
              </div>
              {selectedPlan.jobId && (
                <div className="plan-detail-row">
                  <strong>Job ID:</strong> {selectedPlan.jobId}
                </div>
              )}
              {selectedPlan.error?.message && selectedPlan.status !== "failed" && (
                <div className="plan-detail-row">
                  <strong>Error:</strong>{" "}
                  <span className="error-text">{selectedPlan.error.message}</span>
                </div>
              )}
              {selectedPlan.creditsCharged !== undefined && (
                <div className="plan-detail-row">
                  <strong>Credits Charged:</strong> {selectedPlan.creditsCharged}
                </div>
              )}
              {selectedPlan.creditsDeducted !== undefined && (
                <div className="plan-detail-row">
                  <strong>Credits Deducted:</strong>{" "}
                  {selectedPlan.creditsDeducted ? "Yes" : "No"}
                </div>
              )}
              {selectedPlan.lastRegeneratedAt && (
                <div className="plan-detail-row">
                  <strong>Last Regenerated:</strong>{" "}
                  {formatDate(selectedPlan.lastRegeneratedAt)}
                </div>
              )}
            </div>

            {/* Secondary Activity dump — not a primary section */}
            {!detailLoading ? (
            <div className="work-brief-founder-sections" data-testid="work-brief-founder-sections">
              {linkedRoadmapItem && (
                <div className="plan-content-block" data-testid="work-brief-linked-roadmap">
                  <h5>Linked roadmap item</h5>
                  <p className="work-brief-goal">
                    <strong>{linkedRoadmapItem.title}</strong>
                    {linkedRoadmapItem.lane ? ` · ${linkedRoadmapItem.lane}` : ""}
                    {linkedRoadmapItem.deliveryStatus
                      ? ` · ${linkedRoadmapItem.deliveryStatus}`
                      : ""}
                  </p>
                  {linkedRoadmapItem.outcome ? (
                    <pre className="plan-intelligence-text">{linkedRoadmapItem.outcome}</pre>
                  ) : null}
                  {typeof onOpenRoadmap === "function" && (
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => onOpenRoadmap(linkedRoadmapItem.id)}
                      data-testid="work-brief-open-roadmap"
                    >
                      Open in Roadmap
                    </button>
                  )}
                </div>
              )}
              <div className="plan-content-block" data-testid="work-brief-what-requested">
                <h5>What was requested</h5>
                <pre className="plan-intelligence-text">
                  {selectedPlan.featureRequestText || "No task description"}
                </pre>
              </div>
              <WorkBriefContextSnapshot snapshot={selectedPlan.workBriefContextSnapshot} />
              {(selectedPlan.planIntelligence?.businessValue ||
                selectedPlan.planIntelligence?.goal) && (
                <div className="plan-content-block" data-testid="work-brief-why-matters">
                  <h5>Why this matters</h5>
                  {selectedPlan.planIntelligence.goal && (
                    <p className="work-brief-goal">
                      <strong>Goal:</strong> {selectedPlan.planIntelligence.goal}
                    </p>
                  )}
                  {selectedPlan.planIntelligence.businessValue && (
                    <pre className="plan-intelligence-text">
                      {selectedPlan.planIntelligence.businessValue}
                    </pre>
                  )}
                  {(selectedPlan.planIntelligence.complexity?.level ||
                    selectedPlan.planIntelligence.risk?.level) && (
                    <p className="work-brief-estimates">
                      {selectedPlan.planIntelligence.complexity?.level ? (
                        <span>Complexity: {selectedPlan.planIntelligence.complexity.level}</span>
                      ) : null}
                      {selectedPlan.planIntelligence.complexity?.level &&
                      selectedPlan.planIntelligence.risk?.level
                        ? " · "
                        : null}
                      {selectedPlan.planIntelligence.risk?.level ? (
                        <span>Risk: {selectedPlan.planIntelligence.risk.level}</span>
                      ) : null}
                    </p>
                  )}
                </div>
              )}
              {Array.isArray(selectedPlan.acceptanceCriteria) &&
                selectedPlan.acceptanceCriteria.length > 0 && (
                  <div className="plan-content-block" data-testid="work-brief-acceptance-criteria">
                    <h5>Acceptance criteria</h5>
                    <ul className="plan-intelligence-list">
                      {selectedPlan.acceptanceCriteria.map((criterion, index) => (
                        <li key={index}>{criterion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              <div className="plan-content-block" data-testid="work-brief-guide-rules">
                <WorkBriefGuidePreview
                  variant="detail"
                  entries={detailGuidePreview.entries}
                  loading={guideLoading}
                  error={guideError}
                />
              </div>
              {(selectedPlan.planIntelligence?.pmSummary ||
                selectedPlan.planIntelligence?.engineerSummary) && (
                <div
                  className="plan-content-block"
                  data-testid="work-brief-implementation-brief"
                >
                  <h5>Implementation brief</h5>
                  {selectedPlan.planIntelligence.pmSummary && (
                    <div className="plan-intelligence-section">
                      <h6>PM summary</h6>
                      <pre className="plan-intelligence-text">
                        {selectedPlan.planIntelligence.pmSummary}
                      </pre>
                    </div>
                  )}
                  {selectedPlan.planIntelligence.engineerSummary && (
                    <div className="plan-intelligence-section">
                      <h6>Engineer summary</h6>
                      <pre className="plan-intelligence-text">
                        {selectedPlan.planIntelligence.engineerSummary}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              {selectedPlan.status === "completed" && (
                <div className="plan-content-block" data-testid="work-brief-developer-handoff">
                  <h5>Developer Handoff / Build Prompt</h5>
                  <p className="work-brief-handoff-hint">
                    Use Build Prompt below to copy a ready-to-paste developer handoff for this {TASK_LABEL}
                    (steps, files to touch, constraints).
                  </p>
                </div>
              )}
              {(selectedPlan.userNotes?.trim() || selectedPlan.status === "completed") && (
                <div className="plan-content-block" data-testid="work-brief-planning-notes">
                  <h5>Planning notes</h5>
                  {selectedPlan.userNotes?.trim() ? (
                    <pre className="plan-intelligence-text">{selectedPlan.userNotes}</pre>
                  ) : (
                    <p className="work-brief-handoff-hint">
                      No planning notes yet. Add them from the Roadmap tab (Edit notes).
                    </p>
                  )}
                </div>
              )}
              {selectedPlan.status === "completed" && (
                <div className="plan-content-block" data-testid="work-brief-delivery">
                  <h5>Delivery / branch</h5>
                  {latestDeliveryReview?.reviewSource?.branchName ? (
                    <dl className="work-brief-delivery-dl">
                      <div>
                        <dt>Branch</dt>
                        <dd>{latestDeliveryReview.reviewSource.branchName}</dd>
                      </div>
                      {latestDeliveryReview.reviewSource.baseRef ? (
                        <div>
                          <dt>Base</dt>
                          <dd>{latestDeliveryReview.reviewSource.baseRef}</dd>
                        </div>
                      ) : null}
                      {latestDeliveryReview.aggregate ? (
                        <div>
                          <dt>Latest Acceptance Review</dt>
                          <dd>
                            Met {latestDeliveryReview.aggregate.met} · Partial{" "}
                            {latestDeliveryReview.aggregate.partiallyMet} · Cannot verify{" "}
                            {latestDeliveryReview.aggregate.cannotVerify} · Not met{" "}
                            {latestDeliveryReview.aggregate.notMet}
                          </dd>
                        </div>
                      ) : (
                        <div>
                          <dt>Latest Acceptance Review</dt>
                          <dd>{latestDeliveryReview.status || "Available below"}</dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <p className="work-brief-handoff-hint">
                      No delivery branch recorded yet. Run Acceptance Review below after work lands on a branch.
                    </p>
                  )}
                </div>
              )}
            </div>
            ) : null}

            <LightweightDiagramsPanel
              bundle={selectedPlan.lightweightDiagrams}
              planStatus={selectedPlan.status}
            />

            {/* Plan Content Section */}
            {selectedPlan.status === "completed" && (
              <div className="plan-content-section">
                <h4>Plan Content</h4>

                {/* Work Receipt (Phase 3.2) */}
                <div className="plan-content-block work-receipt-block">
                  <h5>Work Receipt</h5>
                  {selectedPlan.workReceipt ? (
                    <dl className="work-receipt-dl">
                      <dt>Retrieval</dt>
                      <dd>{selectedPlan.workReceipt.retrievalCounts.chunksRetrieved} chunks, {selectedPlan.workReceipt.retrievalCounts.uniqueFiles} files ({selectedPlan.workReceipt.retrievalCounts.codeChunks} code, {selectedPlan.workReceipt.retrievalCounts.docChunks} doc)</dd>
                      <dt>Pruning</dt>
                      <dd>{selectedPlan.workReceipt.pruning.evidenceDroppedSnippetsCount} snippets dropped, {selectedPlan.workReceipt.pruning.evidenceDroppedFilesCount} files dropped{selectedPlan.workReceipt.pruning.reasons?.length ? ` — ${selectedPlan.workReceipt.pruning.reasons.join(", ")}` : ""}</dd>
                      <dt>Timing</dt>
                      <dd>Started {selectedPlan.workReceipt.timing.startedAt}, completed {selectedPlan.workReceipt.timing.completedAt} ({selectedPlan.workReceipt.timing.durationMs}ms)</dd>
                      <dt>Credits</dt>
                      <dd>{selectedPlan.workReceipt.credits.creditsCharged} charged, {selectedPlan.workReceipt.credits.creditsDeducted ? "deducted" : "not deducted"}</dd>
                      <dt>Surface</dt>
                      <dd><span className={`status-badge surface-confidence ${(selectedPlan.workReceipt.surface.surfaceConfidence || "").toLowerCase()}`}>{selectedPlan.workReceipt.surface.surfaceConfidence}</span> — {selectedPlan.workReceipt.surface.surfaceFileCount} files, {selectedPlan.workReceipt.surface.surfaceSymbolCount} symbols</dd>
                    </dl>
                  ) : (
                    <p className="work-receipt-unavailable">Receipt unavailable for older plans.</p>
                  )}
                </div>

                {/* Technical plan details (secondary; founder brief is above) */}
                {selectedPlan.planIntelligence && (
                  <details
                    className="plan-content-block plan-intelligence-block"
                    data-testid="plan-intelligence-block"
                  >
                    <summary>Technical plan details</summary>
                    {selectedPlan.planIntelligence.confidence && (
                      <p className="plan-intelligence-confidence">
                        Confidence:{" "}
                        <span className={`status-badge surface-confidence ${selectedPlan.planIntelligence.confidence.level}`}>
                          {selectedPlan.planIntelligence.confidence.level}
                        </span>
                      </p>
                    )}
                    {selectedPlan.planIntelligence.files?.primary?.length > 0 && (
                      <div className="plan-intelligence-section">
                        <h6>Primary Files</h6>
                        <ul className="plan-intelligence-file-list">
                          {selectedPlan.planIntelligence.files.primary.map((f, i) => (
                            <li key={i}>
                              <code className="file-path-code">{f.path}</code>
                              <span className="plan-intelligence-file-reason"> — {f.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedPlan.planIntelligence.files?.risky?.length > 0 && (
                      <div className="plan-intelligence-section">
                        <h6>Risky Files</h6>
                        <ul className="plan-intelligence-file-list">
                          {selectedPlan.planIntelligence.files.risky.map((f, i) => (
                            <li key={i}>
                              <code className="file-path-code">{f.path}</code>
                              <span className="plan-intelligence-file-reason"> — {f.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedPlan.planIntelligence.files?.avoid?.length > 0 && (
                      <div className="plan-intelligence-section">
                        <h6>Files to Avoid</h6>
                        <ul className="plan-intelligence-file-list">
                          {selectedPlan.planIntelligence.files.avoid.slice(0, 12).map((f, i) => (
                            <li key={i}>
                              <code className="file-path-code">{f.path}</code>
                              <span className="plan-intelligence-file-reason"> — {f.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedPlan.planIntelligence.expectedChanges?.tests?.length > 0 && (
                      <div className="plan-intelligence-section">
                        <h6>Expected Tests</h6>
                        <ul className="plan-intelligence-list">
                          {selectedPlan.planIntelligence.expectedChanges.tests.map((t, i) => (
                            <li key={i}>{t}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedPlan.planIntelligence.unknowns?.length > 0 && (
                      <div className="plan-intelligence-section">
                        <h6>Unknowns / Questions</h6>
                        <ul className="plan-intelligence-list">
                          {selectedPlan.planIntelligence.unknowns.map((u, i) => (
                            <li key={i}>{u}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedPlan.planIntelligence.cursorPrompt && (
                      <div className="plan-intelligence-section">
                        <h6>Handoff prompt (excerpt)</h6>
                        <pre className="plan-intelligence-text plan-intelligence-cursor-excerpt">
                          {selectedPlan.planIntelligence.cursorPrompt.length > 1200
                            ? `${selectedPlan.planIntelligence.cursorPrompt.slice(0, 1200)}…`
                            : selectedPlan.planIntelligence.cursorPrompt}
                        </pre>
                      </div>
                    )}
                  </details>
                )}

                {/* Plan Steps (Phase 4.2: grouped by phase when planStepGroups present) */}
                {selectedPlan.planStepGroups &&
                  Array.isArray(selectedPlan.planStepGroups) &&
                  selectedPlan.planStepGroups.length > 0 ? (
                  <div className="plan-content-block plan-steps-grouped">
                    <h5>Implementation Steps</h5>
                    {selectedPlan.planStepGroups.map((group, gIdx) => (
                      <div key={gIdx} className="plan-step-group">
                        <h6 className="plan-step-group-name">{group.groupName}</h6>
                        <p className="plan-step-group-rationale">{group.rationale}</p>
                        <ol className="plan-steps-list">
                          {group.steps.map((step, sIdx) => (
                            <li key={sIdx} className="plan-step-item">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                ) : selectedPlan.planSteps &&
                  (Array.isArray(selectedPlan.planSteps)
                    ? selectedPlan.planSteps.length > 0
                    : typeof selectedPlan.planSteps === "string" &&
                      selectedPlan.planSteps.trim()) ? (
                  <div className="plan-content-block">
                    <h5>Implementation Steps</h5>
                    <ol className="plan-steps-list">
                      {Array.isArray(selectedPlan.planSteps)
                        ? selectedPlan.planSteps.map((step, index) => (
                            <li key={index} className="plan-step-item">
                              {step}
                            </li>
                          ))
                        : selectedPlan.planSteps
                            .split("\n")
                            .filter((line) => line.trim())
                            .map((step, index) => (
                              <li key={index} className="plan-step-item">
                                {step.trim()}
                              </li>
                            ))}
                    </ol>
                  </div>
                ) : null}

                {/* Risks */}
                {selectedPlan.risks &&
                  (Array.isArray(selectedPlan.risks)
                    ? selectedPlan.risks.length > 0
                    : typeof selectedPlan.risks === "string" &&
                      selectedPlan.risks.trim()) && (
                    <div className="plan-content-block">
                      <h5>Risks & Considerations</h5>
                      <ul className="plan-risks-list">
                        {Array.isArray(selectedPlan.risks)
                          ? selectedPlan.risks.map((risk, index) => (
                              <li key={index} className="plan-risk-item">
                                {risk}
                              </li>
                            ))
                          : selectedPlan.risks
                              .split("\n")
                              .filter((line) => line.trim())
                              .map((risk, index) => (
                                <li key={index} className="plan-risk-item">
                                  {risk.trim()}
                                </li>
                              ))}
                      </ul>
                    </div>
                  )}

                {/* Excluded Files */}
                {selectedPlan.excludedFiles &&
                  Array.isArray(selectedPlan.excludedFiles) &&
                  selectedPlan.excludedFiles.length > 0 && (
                    <div className="plan-content-block">
                      <h5>Excluded Files</h5>
                      <ul className="plan-excluded-files-list">
                        {selectedPlan.excludedFiles.map((filePath, index) => (
                          <li key={index} className="plan-excluded-file-item">
                            <code className="file-path-code">{filePath}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Change Surface (Milestone 1.2) */}
                <div className="plan-content-block">
                  <h5>Change Surface</h5>
                  {selectedPlan.modificationSurface &&
                  Array.isArray(selectedPlan.modificationSurface) &&
                  selectedPlan.modificationSurface.length > 0 ? (
                    <>
                      <div className="change-surface-meta">
                        {selectedPlan.surfaceConfidence && (
                          <span className={`status-badge surface-confidence ${(selectedPlan.surfaceConfidence || "").toLowerCase()}`}>
                            {selectedPlan.surfaceConfidence}
                          </span>
                        )}
                        {selectedPlan.surfaceStats &&
                          (selectedPlan.surfaceStats.fileCount > 0 ||
                            selectedPlan.surfaceStats.symbolCount > 0) && (
                            <span className="change-surface-stats">
                              {selectedPlan.surfaceStats.fileCount} files, {selectedPlan.surfaceStats.symbolCount} symbols
                            </span>
                          )}
                      </div>
                      {(() => {
                        const items = selectedPlan.modificationSurface;
                        const byFile = {};
                        items.forEach((it) => {
                          const fp = it.filePath || "";
                          if (!byFile[fp]) byFile[fp] = [];
                          byFile[fp].push(it);
                        });
                        const sortedPaths = Object.keys(byFile).sort((a, b) => a.localeCompare(b));
                        sortedPaths.forEach((fp) => {
                          byFile[fp].sort((a, b) =>
                            (a.symbolName || "").localeCompare(b.symbolName || "")
                          );
                        });
                        return (
                          <div className="change-surface-groups">
                            {sortedPaths.map((filePath) => (
                              <div key={filePath} className="change-surface-file-group">
                                <div className="change-surface-file-path">
                                  <code className="file-path-code">{filePath}</code>
                                </div>
                                <ul className="change-surface-symbol-list">
                                  {byFile[filePath].map((it, idx) => (
                                    <li key={idx} className="change-surface-symbol-row">
                                      <span className="change-surface-symbol-name">{it.symbolName}</span>
                                      <span className="change-surface-symbol-type">{it.unitType}</span>
                                      <span className="change-surface-symbol-layer">{it.layer}</span>
                                      <span className="change-surface-symbol-confidence">
                                        {typeof it.confidence === "number"
                                          ? `${Math.round(it.confidence * 100)}%`
                                          : "—"}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <p className="change-surface-empty">No change surface computed.</p>
                  )}
                </div>

                {/* Impact Preview (Milestone 1.5) */}
                {selectedPlan.impactPreview ? (
                  <div className="plan-content-block impact-preview-block">
                    <h5>Impact Preview</h5>
                    {(() => {
                      const ip = selectedPlan.impactPreview;
                      const cap = 20;
                      const confidence = selectedPlan.impactConfidence || "—";
                      return (
                        <>
                          <div className="impact-preview-meta">
                            <span className={`status-badge impact-confidence ${(confidence + "").toLowerCase()}`}>
                              {confidence}
                            </span>
                            <span className="impact-preview-stats">
                              {ip.stats?.surfaceFiles ?? 0} surface files, {ip.stats?.surfaceSymbols ?? 0} symbols · {ip.stats?.downstreamFiles ?? 0} downstream files, {ip.stats?.downstreamSymbols ?? 0} symbols · {ip.stats?.testsLikely ?? 0} tests likely
                            </span>
                          </div>
                          <p className="impact-preview-public-api">
                            Public API touched: <strong>{ip.publicApiTouched ? "Yes" : "No"}</strong>
                            {ip.publicApiTouched && ip.publicApiItems?.length > 0 && (
                              <> — {ip.publicApiItems.slice(0, cap).map((i) => `${i.filePath}:${i.symbolName}`).join(", ")}{ip.publicApiItems.length > cap ? ` and ${ip.publicApiItems.length - cap} more` : ""}</>
                            )}
                          </p>
                          {ip.downstreamFiles?.length > 0 && (
                            <div className="impact-preview-list">
                              <h6>Downstream files</h6>
                              <ul>
                                {ip.downstreamFiles.slice(0, cap).map((f, idx) => (
                                  <li key={idx}><code>{f.filePath}</code> <span className="impact-reason">({f.depth}, {f.reason})</span></li>
                                ))}
                                {ip.downstreamFiles.length > cap && <li className="impact-more">and {ip.downstreamFiles.length - cap} more</li>}
                              </ul>
                            </div>
                          )}
                          {ip.downstreamSymbols?.length > 0 && (
                            <div className="impact-preview-list">
                              <h6>Downstream symbols</h6>
                              <ul>
                                {ip.downstreamSymbols.slice(0, cap).map((s, idx) => (
                                  <li key={idx}><code>{s.filePath}:{s.symbolName}</code> <span className="impact-reason">(depth {s.depth})</span></li>
                                ))}
                                {ip.downstreamSymbols.length > cap && <li className="impact-more">and {ip.downstreamSymbols.length - cap} more</li>}
                              </ul>
                            </div>
                          )}
                          {ip.testsLikelyImpacted?.length > 0 && (
                            <div className="impact-preview-list">
                              <h6>Tests likely impacted</h6>
                              <ul>
                                {ip.testsLikelyImpacted.slice(0, cap).map((t, idx) => (
                                  <li key={idx}><code>{t.filePath}</code> <span className="impact-reason">({t.reason})</span></li>
                                ))}
                                {ip.testsLikelyImpacted.length > cap && <li className="impact-more">and {ip.testsLikelyImpacted.length - cap} more</li>}
                              </ul>
                            </div>
                          )}
                          {(ip.limits?.partial || ip.limits?.truncated) && ip.limits?.partialReasons?.length > 0 && (
                            <p className="impact-preview-partial">Partial/truncated: {ip.limits.partialReasons.join("; ")}</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  selectedPlan.status === "completed" && (
                    <div className="plan-content-block impact-preview-block">
                      <p className="impact-preview-unavailable">Impact preview not available for this plan.</p>
                    </div>
                  )
                )}

                {/* Evidence Used (Milestone 1.4) */}
                {selectedPlan.evidencePack ? (
                  <div className="plan-content-block evidence-pack-block">
                    <h5>Evidence Used</h5>
                    {(() => {
                      const pack = selectedPlan.evidencePack;
                      const LAYER_ORDER = ["PUBLIC_API", "ENTRYPOINT", "API", "SERVICE", "DB", "CONFIG", "TEST", "UNKNOWN"];
                      const CAP = 20;
                      const byLayer = pack.byLayer || {};
                      const byFile = pack.byFile || {};
                      const totalByLayer = LAYER_ORDER.reduce((s, L) => s + (byLayer[L]?.length || 0), 0);
                      const totalByFile = Object.keys(byFile).reduce((s, k) => s + (byFile[k]?.length || 0), 0);
                      if (totalByLayer === 0 && totalByFile === 0) {
                        return <p className="evidence-pack-empty">No evidence used.</p>;
                      }
                      return (
                        <>
                          <div className="evidence-pack-section">
                            <button
                              type="button"
                              className="evidence-pack-toggle"
                              onClick={() => setEvidenceByLayerExpanded((e) => !e)}
                              aria-expanded={evidenceByLayerExpanded}
                            >
                              {evidenceByLayerExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              <span>Evidence by layer</span>
                              <span className="evidence-pack-count">({totalByLayer} items)</span>
                            </button>
                            {evidenceByLayerExpanded && (
                              <div className="evidence-pack-layer-list">
                                {LAYER_ORDER.map((layer) => {
                                  const items = byLayer[layer] || [];
                                  if (items.length === 0) return null;
                                  const display = items.slice(0, CAP);
                                  const more = items.length - CAP;
                                  return (
                                    <div key={layer} className="evidence-pack-layer-group">
                                      <h6 className="evidence-pack-layer-name">{layer}</h6>
                                      <span className="evidence-pack-layer-count">({items.length})</span>
                                      <ul className="evidence-pack-item-list">
                                        {display.map((e, idx) => (
                                          <li key={e.chunkId || idx} className="evidence-pack-item">
                                            {e.unitName && <span className="evidence-unit-name">{e.unitName}</span>}
                                            {e.unitType && <span className="evidence-unit-type">({e.unitType})</span>}
                                            {typeof e.similarity === "number" && (
                                              <span className="evidence-similarity">{Math.round(e.similarity * 100)}%</span>
                                            )}
                                            {e.snippet && (
                                              <code className="evidence-snippet">{e.snippet.length > 80 ? e.snippet.slice(0, 80) + "…" : e.snippet}</code>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                      {more > 0 && <span className="evidence-pack-more">and {more} more</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="evidence-pack-section">
                            <button
                              type="button"
                              className="evidence-pack-toggle"
                              onClick={() => setEvidenceByFileExpanded((e) => !e)}
                              aria-expanded={evidenceByFileExpanded}
                            >
                              {evidenceByFileExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              <span>Evidence by file</span>
                              <span className="evidence-pack-count">({totalByFile} items)</span>
                            </button>
                            {evidenceByFileExpanded && (
                              <div className="evidence-pack-file-list">
                                {Object.keys(byFile)
                                  .sort((a, b) => a.localeCompare(b))
                                  .map((filePath) => {
                                    const items = byFile[filePath] || [];
                                    const display = items.slice(0, CAP);
                                    const more = items.length - CAP;
                                    return (
                                      <div key={filePath} className="evidence-pack-file-group">
                                        <code className="evidence-pack-file-path">{filePath}</code>
                                        <span className="evidence-pack-file-count">({items.length})</span>
                                        <ul className="evidence-pack-item-list">
                                          {display.map((e, idx) => (
                                            <li key={e.chunkId || idx} className="evidence-pack-item">
                                              {e.unitName && <span className="evidence-unit-name">{e.unitName}</span>}
                                              {typeof e.similarity === "number" && (
                                                <span className="evidence-similarity">{Math.round(e.similarity * 100)}%</span>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                        {more > 0 && <span className="evidence-pack-more">and {more} more</span>}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  selectedPlan.status === "completed" && (
                    <div className="plan-content-block evidence-pack-block">
                      <p className="evidence-pack-unavailable">Evidence pack not available for this plan.</p>
                    </div>
                  )
                )}

                {/* Recommendations (Grouped by Classification) */}
                {selectedPlan.recommendations &&
                  Array.isArray(selectedPlan.recommendations) &&
                  selectedPlan.recommendations.length > 0 && (
                    <div className="plan-content-block">
                      <h5>File Recommendations</h5>
                      {(() => {
                        // Group recommendations by classification
                        const mustEdit = selectedPlan.recommendations.filter(
                          (r) => r.classification === "must_edit"
                        );
                        const likelyEdit = selectedPlan.recommendations.filter(
                          (r) => r.classification === "likely_edit"
                        );
                        const optional = selectedPlan.recommendations.filter(
                          (r) => r.classification === "optional"
                        );

                        return (
                          <>
                            {mustEdit.length > 0 && (
                              <div className="recommendations-group">
                                <h6 className="recommendations-group-header must-edit">
                                  Must Edit ({mustEdit.length})
                                </h6>
                                <ul className="recommendations-list">
                                  {mustEdit.map((rec, index) => (
                                    <li key={index} className="recommendation-item">
                                      <div className="recommendation-file">
                                        <code className="file-path-code">
                                          {rec.filePath}
                                        </code>
                                        {rec.confidence !== undefined && (
                                          <span className="recommendation-confidence">
                                            {(rec.confidence * 100).toFixed(0)}%
                                          </span>
                                        )}
                                      </div>
                                      {rec.rationale && (
                                        <div className="recommendation-rationale">
                                          {rec.rationale}
                                        </div>
                                      )}
                                      {rec.evidence &&
                                        rec.evidence.length > 0 && (
                                          <div className="recommendation-evidence">
                                            <div className="recommendation-evidence-count">
                                              {rec.evidence.length} evidence
                                              {rec.evidence.length !== 1
                                                ? " snippets"
                                                : " snippet"}
                                            </div>
                                            {rec.evidence.map((ev, evIdx) => {
                                              const label = formatEvidenceLabel(ev);
                                              const trustMeta = getEvidenceTrustMeta(ev);
                                              return (
                                                <div key={ev.chunkId || evIdx} className="evidence-snippet-block">
                                                  {label ? (
                                                    <div className="evidence-snippet-label">
                                                      {label}
                                                      {ev.similarity != null && (
                                                        <span className="evidence-similarity">
                                                          {" "}
                                                          {(ev.similarity * 100).toFixed(0)}%
                                                        </span>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="evidence-snippet-label">
                                                      <code>{ev.chunkId}</code>
                                                      {ev.similarity != null && (
                                                        <span className="evidence-similarity">
                                                          {" "}
                                                          {(ev.similarity * 100).toFixed(0)}%
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                                  {trustMeta.trustLabel && (
                                                    <div className={`evidence-trust-label ${trustMeta.trustClass}`}>
                                                      {trustMeta.trustLabel}
                                                    </div>
                                                  )}
                                                  <pre className="evidence-snippet-code">{trustMeta.snippetPreview}</pre>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {likelyEdit.length > 0 && (
                              <div className="recommendations-group">
                                <h6 className="recommendations-group-header likely-edit">
                                  Likely Edit ({likelyEdit.length})
                                </h6>
                                <ul className="recommendations-list">
                                  {likelyEdit.map((rec, index) => (
                                    <li key={index} className="recommendation-item">
                                      <div className="recommendation-file">
                                        <code className="file-path-code">
                                          {rec.filePath}
                                        </code>
                                        {rec.confidence !== undefined && (
                                          <span className="recommendation-confidence">
                                            {(rec.confidence * 100).toFixed(0)}%
                                          </span>
                                        )}
                                      </div>
                                      {rec.rationale && (
                                        <div className="recommendation-rationale">
                                          {rec.rationale}
                                        </div>
                                      )}
                                      {rec.evidence &&
                                        rec.evidence.length > 0 && (
                                          <div className="recommendation-evidence">
                                            <div className="recommendation-evidence-count">
                                              {rec.evidence.length} evidence
                                              {rec.evidence.length !== 1
                                                ? " snippets"
                                                : " snippet"}
                                            </div>
                                            {rec.evidence.map((ev, evIdx) => {
                                              const label = formatEvidenceLabel(ev);
                                              const trustMeta = getEvidenceTrustMeta(ev);
                                              return (
                                                <div key={ev.chunkId || evIdx} className="evidence-snippet-block">
                                                  {label ? (
                                                    <div className="evidence-snippet-label">
                                                      {label}
                                                      {ev.similarity != null && (
                                                        <span className="evidence-similarity">
                                                          {" "}
                                                          {(ev.similarity * 100).toFixed(0)}%
                                                        </span>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="evidence-snippet-label">
                                                      <code>{ev.chunkId}</code>
                                                      {ev.similarity != null && (
                                                        <span className="evidence-similarity">
                                                          {" "}
                                                          {(ev.similarity * 100).toFixed(0)}%
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                                  {trustMeta.trustLabel && (
                                                    <div className={`evidence-trust-label ${trustMeta.trustClass}`}>
                                                      {trustMeta.trustLabel}
                                                    </div>
                                                  )}
                                                  <pre className="evidence-snippet-code">{trustMeta.snippetPreview}</pre>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {optional.length > 0 && (
                              <div className="recommendations-group">
                                <h6 className="recommendations-group-header optional">
                                  Optional ({optional.length})
                                </h6>
                                <ul className="recommendations-list">
                                  {optional.map((rec, index) => (
                                    <li key={index} className="recommendation-item">
                                      <div className="recommendation-file">
                                        <code className="file-path-code">
                                          {rec.filePath}
                                        </code>
                                        {rec.confidence !== undefined && (
                                          <span className="recommendation-confidence">
                                            {(rec.confidence * 100).toFixed(0)}%
                                          </span>
                                        )}
                                      </div>
                                      {rec.rationale && (
                                        <div className="recommendation-rationale">
                                          {rec.rationale}
                                        </div>
                                      )}
                                      {rec.evidence &&
                                        rec.evidence.length > 0 && (
                                          <div className="recommendation-evidence">
                                            <div className="recommendation-evidence-count">
                                              {rec.evidence.length} evidence
                                              {rec.evidence.length !== 1
                                                ? " snippets"
                                                : " snippet"}
                                            </div>
                                            {rec.evidence.map((ev, evIdx) => {
                                              const label = formatEvidenceLabel(ev);
                                              const trustMeta = getEvidenceTrustMeta(ev);
                                              return (
                                                <div key={ev.chunkId || evIdx} className="evidence-snippet-block">
                                                  {label ? (
                                                    <div className="evidence-snippet-label">
                                                      {label}
                                                      {ev.similarity != null && (
                                                        <span className="evidence-similarity">
                                                          {" "}
                                                          {(ev.similarity * 100).toFixed(0)}%
                                                        </span>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="evidence-snippet-label">
                                                      <code>{ev.chunkId}</code>
                                                      {ev.similarity != null && (
                                                        <span className="evidence-similarity">
                                                          {" "}
                                                          {(ev.similarity * 100).toFixed(0)}%
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                                  {trustMeta.trustLabel && (
                                                    <div className={`evidence-trust-label ${trustMeta.trustClass}`}>
                                                      {trustMeta.trustLabel}
                                                    </div>
                                                  )}
                                                  <pre className="evidence-snippet-code">{trustMeta.snippetPreview}</pre>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                {/* Intent JSON (Collapsible) */}
                {selectedPlan.intentJson && (
                  <div className="plan-content-block">
                    <button
                      className="plan-collapsible-header"
                      onClick={() => setIntentJsonExpanded(!intentJsonExpanded)}
                      type="button"
                    >
                      <h5>Intent JSON</h5>
                      {intentJsonExpanded ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </button>
                    {intentJsonExpanded && (
                      <pre className="plan-json-display">
                        <code>
                          {typeof selectedPlan.intentJson === "string"
                            ? selectedPlan.intentJson
                            : JSON.stringify(selectedPlan.intentJson, null, 2)}
                        </code>
                      </pre>
                    )}
                  </div>
                )}

                {/* Empty state - only show if ALL content fields are empty */}
                {(!selectedPlan.planSteps ||
                  (Array.isArray(selectedPlan.planSteps) &&
                    selectedPlan.planSteps.length === 0) ||
                  (typeof selectedPlan.planSteps === "string" &&
                    !selectedPlan.planSteps.trim())) &&
                (!selectedPlan.risks ||
                  (Array.isArray(selectedPlan.risks) &&
                    selectedPlan.risks.length === 0) ||
                  (typeof selectedPlan.risks === "string" &&
                    !selectedPlan.risks.trim())) &&
                (!selectedPlan.recommendations ||
                  !Array.isArray(selectedPlan.recommendations) ||
                  selectedPlan.recommendations.length === 0) &&
                (!selectedPlan.excludedFiles ||
                  (Array.isArray(selectedPlan.excludedFiles) &&
                    selectedPlan.excludedFiles.length === 0)) &&
                !selectedPlan.intentJson &&
                (!selectedPlan.modificationSurface ||
                  selectedPlan.modificationSurface.length === 0) ? (
                  <div className="plan-content-empty">
                    <p>No plan content available yet.</p>
                  </div>
                ) : null}
              </div>
            )}
            </details>

            {/* Action Buttons */}
            <div className="plan-actions">
              {mutateAllowed ? (
                <button
                  className="secondary-button"
                  onClick={handleRegenerate}
                  disabled={
                    selectedPlan.status === "generating" || regenerating
                  }
                >
                  <RefreshCw size={16} />
                  {regenerating ? "Regenerating..." : "Regenerate"}
                </button>
              ) : null}
              <button
                className="secondary-button"
                onClick={handleExport}
                disabled={selectedPlan.status !== "completed" || exporting}
              >
                <Download size={16} />
                {exporting ? "Exporting..." : "Export"}
              </button>
              <button
                className="secondary-button"
                onClick={handleCopyCursorPrompt}
                disabled={selectedPlan.status !== "completed" || copyingPrompt}
                title={
                  handoffAgreementStatus?.ready
                    ? "Copy a build prompt that includes the agreed handoff promises, clarifications, and plan steps."
                    : "You can copy a plan-based prompt, but the handoff promises are not fully agreed yet (product + engineer)."
                }
                data-testid="work-brief-build-prompt"
              >
                <Copy size={16} />
                {copyingPrompt ? "Copying..." : "Build Prompt"}
              </button>
              {selectedPlan.engineerStartPack?.implementationPrompt ? (
                <button
                  className="secondary-button"
                  data-testid="engineer-start-pack-copy"
                  onClick={() =>
                    navigator.clipboard.writeText(selectedPlan.engineerStartPack.implementationPrompt)
                  }
                >
                  Copy implementation prompt
                </button>
              ) : null}
              {copyPromptSuccess && (
                <span className="copy-prompt-success">Copied to clipboard.</span>
              )}
              {handoffAgreementStatus?.ready ? (
                <p
                  className="work-brief-build-prompt-note work-brief-build-prompt-note--ready"
                  data-testid="work-brief-build-prompt-ready"
                >
                  Build Prompt loads the agreed Intent Contract (promises + clarifications) plus the{" "}
                  {TASK_LABEL} plan steps and file targets.
                </p>
              ) : handoffAgreementStatus ? (
                <p
                  className="work-brief-build-prompt-note"
                  data-testid="work-brief-build-prompt-not-ready"
                >
                  Build Prompt still uses the {TASK_LABEL} plan only. It is not a substitute for an
                  agreed handoff — {handoffAgreementStatus.headline}.
                </p>
              ) : null}
              {mutateAllowed ? (
                <button
                  className="secondary-button danger-button"
                  onClick={handleDelete}
                  disabled={Boolean(deletingId)}
                  title={`Delete this ${TASK_LABEL}`}
                >
                  <Trash2 size={16} />
                  {deletingId ? "Deleting…" : `Delete ${TASK_LABEL}`}
                </button>
              ) : null}
            </div>
            </div>
            </TaskWorkspace>
            </TaskFocusWorkspace>
            </>
              </>
            )}
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
};

export default FeaturePlansPanel;
